import { useEffect, useMemo, useState } from "react";
import { useMe } from "../hooks/useMe";
import {
  type AdminProject,
  type AdminUser,
  type CreatedUser,
  type UsageByEmail,
  createUser,
  deleteUser,
  getUserProjects,
  listAllProjects,
  listUserUsage,
  listUsers,
  setUserLimits,
  setUserProjects,
  setUserRole,
} from "../api/admin";
import { IconBack, IconPlus, IconSearch, IconShield, IconTable, IconGrid, IconTrash, IconUser, IconX } from "./admin/icons";
import { Checkbox } from "./admin/MemberParts";
import { MemberRow } from "./admin/MemberRow";
import { MemberCard } from "./admin/MemberCard";
import { ActionMenu } from "./admin/ActionMenu";
import { MemberSheet } from "./admin/MemberSheet";
import { InviteModal, type InvitePayload } from "./admin/InviteModal";
import { toMemberVM, usd, usdShort, type MemberVM } from "./admin/memberModel";
import type { MemberActions, MenuState, SheetFocus } from "./admin/types";
import "./admin/members.css";

type Props = { onClose: () => void };

const FILTERS = [
  { id: "all", label: "All" },
  { id: "admin", label: "Admins" },
  { id: "member", label: "Members" },
  { id: "pending", label: "Pending" },
] as const;
type FilterId = (typeof FILTERS)[number]["id"];

function readLayout(): "table" | "cards" {
  try {
    return localStorage.getItem("taiw.members.layout") === "cards" ? "cards" : "table";
  } catch {
    return "table";
  }
}

export default function AdminUsersPage({ onClose }: Props) {
  const me = useMe();
  const myEmail = me.data?.email ?? null;

  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [projects, setProjects] = useState<AdminProject[] | null>(null);
  const [usage, setUsage] = useState<UsageByEmail>({});
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterId>("all");
  const [sort, setSort] = useState<"name" | "spend">("name");
  const [layout, setLayout] = useState<"table" | "cards">(readLayout);

  const [sel, setSel] = useState<Set<number>>(() => new Set());
  const [openId, setOpenId] = useState<number | null>(null);
  const [sheetFocus, setSheetFocus] = useState<SheetFocus | undefined>(undefined);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [assignedMap, setAssignedMap] = useState<Record<number, string[]>>({});
  const [created, setCreated] = useState<CreatedUser[] | null>(null);

  const refresh = async () => {
    setError(null);
    try {
      const [list, allProjects, usageMap] = await Promise.all([
        listUsers(),
        listAllProjects(),
        listUserUsage().catch(() => ({}) as UsageByEmail),
      ]);
      setUsers(list);
      setProjects(allProjects);
      setUsage(usageMap);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  // Lazily load a member's project assignments the first time their sheet opens.
  useEffect(() => {
    if (openId == null || assignedMap[openId] !== undefined) return;
    let cancelled = false;
    getUserProjects(openId)
      .then((ids) => {
        if (!cancelled) setAssignedMap((prev) => ({ ...prev, [openId]: ids }));
      })
      .catch((e) => {
        if (!cancelled) setError(`Failed to load project access: ${(e as Error).message}`);
      });
    return () => {
      cancelled = true;
    };
    // Only react to the opened member; the early-return guard above reads the
    // latest cache, and setAssignedMap uses a functional update — so omitting
    // assignedMap here avoids a needless re-run on every assignment change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const setLayoutPersist = (l: "table" | "cards") => {
    setLayout(l);
    try {
      localStorage.setItem("taiw.members.layout", l);
    } catch {
      /* private mode / no storage — non-fatal */
    }
  };

  // ---- view-models -------------------------------------------------------
  const members = useMemo<MemberVM[]>(
    () => (users ?? []).map((u) => toMemberVM(u, usage[u.email], myEmail)),
    [users, usage, myEmail],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = members;
    if (q) list = list.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
    if (filter === "admin") list = list.filter((m) => m.role === "admin");
    else if (filter === "member") list = list.filter((m) => m.role === "member");
    else if (filter === "pending") list = list.filter((m) => m.status === "pending");
    return [...list].sort((a, b) =>
      sort === "spend" ? b.spentCents - a.spentCents : a.name.localeCompare(b.name),
    );
  }, [members, query, filter, sort]);

  const counts = useMemo(() => {
    const admins = members.filter((m) => m.role === "admin").length;
    const memberRole = members.filter((m) => m.role === "member").length;
    const pending = members.filter((m) => m.status === "pending").length;
    const active = members.filter((m) => m.status === "active").length;
    const today = members.reduce((s, m) => s + m.todayCents, 0);
    const month = members.reduce((s, m) => s + m.spentCents, 0);
    const hasCap = members.some((m) => m.monthlyCapCents != null);
    const cap = members.reduce((s, m) => s + (m.monthlyCapCents ?? 0), 0);
    return { total: members.length, admins, memberRole, pending, active, today, month, hasCap, cap };
  }, [members]);

  // ---- selection ---------------------------------------------------------
  const visIds = filtered.map((m) => m.id);
  const selVis = visIds.filter((id) => sel.has(id));
  const allSel = visIds.length > 0 && selVis.length === visIds.length;
  const someSel = selVis.length > 0 && !allSel;
  const toggleSel = (id: number) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleAll = () =>
    setSel((s) => {
      const n = new Set(s);
      if (allSel) visIds.forEach((id) => n.delete(id));
      else visIds.forEach((id) => n.add(id));
      return n;
    });
  const clearSel = () => setSel(new Set());

  // ---- mutations ---------------------------------------------------------
  const patchUser = (id: number, patch: Partial<AdminUser>) =>
    setUsers((prev) => (prev === null ? prev : prev.map((u) => (u.id === id ? { ...u, ...patch } : u))));

  const doSetRole = async (id: number, role: "admin" | "member") => {
    patchUser(id, { role });
    try {
      await setUserRole(id, role);
    } catch (e) {
      setError(`Failed to update role: ${(e as Error).message}`);
      void refresh();
    }
  };

  const doSetLimit = async (id: number, field: "daily" | "monthly", dollars: number | null) => {
    const cents = dollars === null ? null : Math.round(dollars * 100);
    patchUser(id, field === "daily" ? { daily_usd_cents: cents } : { monthly_usd_cents: cents });
    try {
      await setUserLimits(id, field === "daily" ? { dailyUsd: dollars } : { monthlyUsd: dollars });
    } catch (e) {
      setError(`Failed to save ${field} cap: ${(e as Error).message}`);
      void refresh();
    }
  };

  const doToggleProject = async (id: number, projectId: string, next: boolean) => {
    const cur = assignedMap[id] ?? [];
    const updated = next ? [...new Set([...cur, projectId])] : cur.filter((x) => x !== projectId);
    setAssignedMap((prev) => ({ ...prev, [id]: updated }));
    try {
      await setUserProjects(id, updated);
    } catch (e) {
      setError(`Failed to update project access: ${(e as Error).message}`);
      setAssignedMap((prev) => ({ ...prev, [id]: cur }));
    }
  };

  const doRemove = async (id: number) => {
    const target = members.find((m) => m.id === id);
    if (target?.you) return;
    if (!confirm(`Remove ${target?.email ?? "this member"} from the workspace? Their conversations will be deleted.`))
      return;
    try {
      await deleteUser(id);
      setSel((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      if (openId === id) setOpenId(null);
      await refresh();
    } catch (e) {
      setError(`Failed to remove member: ${(e as Error).message}`);
    }
  };

  const doInvite = async (payload: InvitePayload) => {
    const createdList: CreatedUser[] = [];
    const failures: string[] = [];
    for (const rawEmail of payload.emails) {
      const email = rawEmail.trim().toLowerCase();
      try {
        const c = await createUser(email, payload.role);
        createdList.push(c);
        if (payload.monthlyDollars != null) {
          try {
            await setUserLimits(c.id, { monthlyUsd: payload.monthlyDollars });
          } catch {
            /* non-fatal: account exists, budget can be set later */
          }
        }
        if (payload.projectIds.length) {
          try {
            await setUserProjects(c.id, payload.projectIds);
          } catch {
            /* non-fatal: account exists, access can be granted later */
          }
        }
      } catch (e) {
        failures.push(`${email} (${(e as Error).message})`);
      }
    }

    if (createdList.length) {
      setCreated(createdList);
      setInviteOpen(false);
    }
    await refresh();

    if (failures.length) {
      const msg = `Could not invite: ${failures.join(", ")}`;
      // Surface the failures without discarding any temp passwords already shown.
      setError(msg);
      if (!createdList.length) throw new Error(msg);
    }
  };

  const bulkRole = (role: "admin" | "member") => {
    selVis.forEach((id) => {
      const m = members.find((x) => x.id === id);
      if (m && !(role === "member" && m.you && m.role === "admin")) void doSetRole(id, role);
    });
  };
  const bulkRemove = async () => {
    const ids = selVis.filter((id) => !members.find((m) => m.id === id)?.you);
    if (!ids.length) return;
    if (!confirm(`Remove ${ids.length} member${ids.length === 1 ? "" : "s"} from the workspace? Their conversations will be deleted.`))
      return;
    for (const id of ids) {
      try {
        await deleteUser(id);
      } catch (e) {
        setError(`Failed to remove a member: ${(e as Error).message}`);
      }
    }
    clearSel();
    if (openId != null && ids.includes(openId)) setOpenId(null);
    await refresh();
  };

  const openMenu = (id: number, anchor: HTMLElement) => {
    const r = anchor.getBoundingClientRect();
    setMenu({ id, x: r.right - 196, y: r.bottom + 6 });
  };

  const actions: MemberActions = {
    open: (id, focus) => {
      setOpenId(id);
      setSheetFocus(focus);
      setMenu(null);
    },
    setRole: (id, role) => {
      void doSetRole(id, role);
      setMenu(null);
    },
    remove: (id) => {
      void doRemove(id);
      setMenu(null);
    },
  };

  const openMember = openId != null ? members.find((m) => m.id === openId) ?? null : null;
  const menuMember = menu ? members.find((m) => m.id === menu.id) ?? null : null;
  const capStat = counts.hasCap ? `/ ${usdShort(counts.cap)} cap` : "no cap set";

  return (
    <div className="members members-screen">
      <div className="members-head">
        <div className="members-head-inner">
          <div className="members-title-row">
            <div className="members-title">
              <h2>Manage users</h2>
              <p>Roles, token budgets, and project access for everyone in the Ptero-Cloud workspace.</p>
            </div>
            <div className="members-head-actions">
              <button className="btn btn-ghost" onClick={onClose}>
                <IconBack /> Back to chat
              </button>
              <button className="btn btn-accent" onClick={() => setInviteOpen(true)}>
                <IconPlus /> Invite member
              </button>
            </div>
          </div>

          <div className="mstats">
            <div className="mstat">
              <span className="mstat-k">Members</span>
              <span className="mstat-v">
                {counts.total}
                <small>{counts.active} active</small>
              </span>
            </div>
            <div className="mstat">
              <span className="mstat-k">Admins</span>
              <span className="mstat-v">{counts.admins}</span>
            </div>
            <div className="mstat">
              <span className="mstat-k">Pending 2FA</span>
              <span className="mstat-v">{counts.pending}</span>
            </div>
            <div className="mstat">
              <span className="mstat-k">Spend · today</span>
              <span className="mstat-v">{usd(counts.today)}</span>
            </div>
            <div className="mstat">
              <span className="mstat-k">Spend · 30 days</span>
              <span className="mstat-v">
                {usd(counts.month)}
                <small>{capStat}</small>
              </span>
            </div>
          </div>

          <div className="mtoolbar">
            <div className="msearch">
              <IconSearch />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or email…"
                aria-label="Search members"
              />
            </div>
            <div className="mfilters" role="tablist" aria-label="Filter members">
              {FILTERS.map((f) => {
                const c =
                  f.id === "all"
                    ? counts.total
                    : f.id === "admin"
                      ? counts.admins
                      : f.id === "member"
                        ? counts.memberRole
                        : counts.pending;
                return (
                  <button
                    key={f.id}
                    role="tab"
                    aria-selected={filter === f.id}
                    className={`mfilter${filter === f.id ? " on" : ""}`}
                    onClick={() => setFilter(f.id)}
                  >
                    {f.label}
                    <span className="fc">{c}</span>
                  </button>
                );
              })}
            </div>
            <div className="mtoolbar-spacer" />
            <div className="layout-toggle" role="group" aria-label="Layout">
              <button
                className={layout === "table" ? "on" : ""}
                onClick={() => setLayoutPersist("table")}
                aria-pressed={layout === "table"}
                title="Table view"
              >
                <IconTable />
              </button>
              <button
                className={layout === "cards" ? "on" : ""}
                onClick={() => setLayoutPersist("cards")}
                aria-pressed={layout === "cards"}
                title="Card view"
              >
                <IconGrid />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="members-body">
        <div className="members-body-inner">
          {error && (
            <div className="members-alert" role="alert">
              <span>{error}</span>
              <button onClick={() => setError(null)} aria-label="Dismiss">
                <IconX />
              </button>
            </div>
          )}

          {created && created.length > 0 && (
            <div className="invite-results" role="status">
              <div className="invite-results-head">
                <h3>{created.length === 1 ? "Account created" : `${created.length} accounts created`} — share once</h3>
                <button className="btn btn-ghost" onClick={() => setCreated(null)}>
                  Dismiss
                </button>
              </div>
              <p>
                Send each person their temporary password over a secure channel. They must change it and
                set up 2FA on first login. These passwords are shown only now.
              </p>
              <ul>
                {created.map((c) => (
                  <li key={c.id}>
                    <code className="cred-email">{c.email}</code>
                    <code className="cred-pass">{c.tempPassword}</code>
                    <span className={`rolebadge ${c.role}`}>{c.role === "admin" ? "Admin" : "Member"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {selVis.length > 0 && (
            <div className="bulkbar">
              <Checkbox on={allSel} mixed={someSel} onClick={toggleAll} label={allSel ? "Deselect all visible" : someSel ? "Select all visible (some selected)" : "Select all visible"} />
              <span className="bulkbar-count">
                <b>{selVis.length}</b> selected
              </span>
              <div className="bulkbar-actions">
                <button className="btn" onClick={() => bulkRole("admin")}>
                  <IconShield style={{ width: 13, height: 13 }} /> Make admin
                </button>
                <button className="btn" onClick={() => bulkRole("member")}>
                  <IconUser style={{ width: 13, height: 13 }} /> Make member
                </button>
                <button className="btn btn-danger" onClick={() => void bulkRemove()}>
                  <IconTrash style={{ width: 13, height: 13 }} /> Remove
                </button>
                <button className="btn btn-ghost" onClick={clearSel}>
                  Clear
                </button>
              </div>
            </div>
          )}

          {users === null ? (
            <div className="members-empty">Loading members…</div>
          ) : filtered.length === 0 ? (
            <div className="members-empty">No members match “{query || filter}”.</div>
          ) : layout === "cards" ? (
            <div className="ucards">
              {filtered.map((m) => (
                <MemberCard
                  key={m.id}
                  m={m}
                  selected={sel.has(m.id)}
                  onSelect={toggleSel}
                  onOpen={(id) => actions.open(id)}
                  onMenu={openMenu}
                />
              ))}
            </div>
          ) : (
            <div className="utable">
              <div className="urow uhead">
                <div className="ucell-check">
                  <Checkbox on={allSel} mixed={someSel} onClick={toggleAll} label={allSel ? "Deselect all visible" : someSel ? "Select all visible (some selected)" : "Select all visible"} />
                </div>
                <div>
                  <span className="uh-sortable" onClick={() => setSort("name")}>
                    Member {sort === "name" && <span className="uh-active">↓</span>}
                  </span>
                </div>
                <div>Role</div>
                <div className="ucell-spend">
                  <span className="uh-sortable" onClick={() => setSort("spend")}>
                    Spend / budget {sort === "spend" && <span className="uh-active">↓</span>}
                  </span>
                </div>
                <div className="ucell-active">Last active</div>
                <div />
              </div>
              {filtered.map((m) => (
                <MemberRow
                  key={m.id}
                  m={m}
                  selected={sel.has(m.id)}
                  onSelect={toggleSel}
                  onOpen={(id) => actions.open(id)}
                  onMenu={openMenu}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {menuMember && menu && (
        <ActionMenu m={menuMember} pos={menu} onClose={() => setMenu(null)} actions={actions} />
      )}

      {openMember && projects && (
        <MemberSheet
          m={openMember}
          projects={projects}
          assignedIds={assignedMap[openMember.id] ?? null}
          initialFocus={sheetFocus}
          onClose={() => {
            setOpenId(null);
            setSheetFocus(undefined);
          }}
          onSetRole={doSetRole}
          onSetMonthly={(id, d) => void doSetLimit(id, "monthly", d)}
          onSetDaily={(id, d) => void doSetLimit(id, "daily", d)}
          onToggleProject={doToggleProject}
          onRemove={doRemove}
        />
      )}

      {inviteOpen && (
        <InviteModal projects={projects ?? []} onClose={() => setInviteOpen(false)} onInvite={doInvite} />
      )}
    </div>
  );
}
